import { z } from 'zod'

/**
 * Configurações do sistema, guardadas como chave/valor jsonb.
 * O schema por chave mora aqui para a tela e a API validarem o mesmo.
 */

export const CHAVES_CONFIGURACAO = {
  cronometro_primeira_resposta_horas: z.number().int().min(1).max(720),
  inatividade_ativa: z.boolean(),
  inatividade_horas: z.number().int().min(1).max(8760),
  carteira_expira_horas: z.number().int().min(1).max(8760),
  /**
   * Supervisão (5.6). Zero desliga o aviso.
   *
   * Separado do cronômetro de propósito: o cronômetro cobra o responsável em
   * 24h, e este avisa quem toma conta da equipe. Um pode ser mais curto que o
   * outro sem que mexer num mude o outro.
   */
  supervisao_sem_resposta_horas: z.number().int().min(0).max(8760),
  supervisao_fila_horas: z.number().int().min(0).max(8760),
  lote_intervalo_min_segundos: z.number().int().min(5).max(3600),
  lote_intervalo_max_segundos: z.number().int().min(5).max(7200),
  lote_teto_diario: z.number().int().min(1).max(5000),
  fuso_horario: z.string().min(1).max(60),
  historico_dias_para_reabrir: z.number().int().min(0).max(365),
  setor_padrao_id: z.string().max(64),
} as const

export type ChaveConfiguracao = keyof typeof CHAVES_CONFIGURACAO

/**
 * Como cada configuração se apresenta na tela.
 *
 * O rótulo era a frase inteira — "Horas que o contato fica com o vendedor após
 * encerrar por inatividade" — e a chave técnica ficava logo abaixo, à mostra.
 * Dava uma lista em ordem alfabética de nome de coluna de banco, onde coisas
 * que só funcionam juntas apareciam separadas e nada dizia o que ia acontecer
 * ao mudar um número.
 *
 * Aqui cada uma tem nome curto, uma frase dizendo **o que acontece**, e a
 * unidade sai do nome e vai para o lado do campo, onde se digita.
 */

export type GrupoConfiguracao = 'atendimento' | 'supervisao' | 'fuso' | 'disparo' | 'importacao'

export interface DescricaoConfiguracao {
  grupo: GrupoConfiguracao
  /** Nome curto, do jeito que se fala. */
  nome: string
  /** Para que o ajuste existe. Uma linha, sempre à vista embaixo do nome. */
  objetivo: string
  /** Aparece colado no campo: "horas", "dias", "segundos". */
  unidade?: string
  /** O que zero significa, quando significa alguma coisa. */
  zeroDesliga?: string
  /** Só tem efeito enquanto esta outra chave estiver ligada. */
  dependeDe?: ChaveConfiguracao
}

export const GRUPOS_CONFIGURACAO: {
  id: GrupoConfiguracao
  titulo: string
  descricao: string
}[] = [
  {
    id: 'atendimento',
    titulo: 'Atendimento',
    descricao: 'Quanto tempo uma conversa pode ficar parada antes de o sistema agir sozinho.',
  },
  {
    id: 'supervisao',
    titulo: 'Avisos para a supervisão',
    descricao:
      'Chegam no sino do proprietário e de quem tem a permissão "Receber avisos de supervisão". Zero desliga o aviso.',
  },
  {
    /*
      Era "Horário de funcionamento", com os dias e horas da empresa. Desde
      10/10/2026 o horário é de cada pessoa, na janela de permissões dela, e
      aqui fica só o relógio em que ele é medido.
    */
    id: 'fuso',
    titulo: 'Fuso horário',
    descricao: 'O relógio em que o sistema conta as horas.',
  },
  {
    id: 'disparo',
    titulo: 'Disparo em lote',
    descricao:
      'O ritmo do envio em massa. Mandar rápido demais é o caminho mais curto para o número ser bloqueado.',
  },
  {
    id: 'importacao',
    titulo: 'Importação de histórico',
    descricao: 'Vale só na hora de trazer conversas de fora para dentro do CRM.',
  },
]

export const DESCRICAO_CONFIGURACAO: Record<ChaveConfiguracao, DescricaoConfiguracao> = {
  cronometro_primeira_resposta_horas: {
    grupo: 'atendimento',
    nome: 'Prazo para a primeira resposta',
    objetivo: 'Fixar em quanto tempo o cliente precisa ter recebido resposta de alguém.',
    unidade: 'horas',
  },
  inatividade_ativa: {
    grupo: 'atendimento',
    nome: 'Encerrar conversa parada sozinho',
    objetivo: 'Decidir se o sistema fecha por conta própria as conversas sem movimento.',
  },
  inatividade_horas: {
    grupo: 'atendimento',
    nome: 'Tempo parado até encerrar',
    objetivo: 'Definir quanto tempo sem mensagem faz a conversa ser encerrada.',
    unidade: 'horas',
    dependeDe: 'inatividade_ativa',
  },
  carteira_expira_horas: {
    grupo: 'atendimento',
    nome: 'Reserva do cliente depois do encerramento automático',
    objetivo:
      'Dar ao vendedor um prazo de preferência sobre o cliente depois de a conversa fechar sozinha.',
    unidade: 'horas',
    dependeDe: 'inatividade_ativa',
  },
  supervisao_sem_resposta_horas: {
    grupo: 'supervisao',
    nome: 'Cliente esperando resposta',
    objetivo: 'Avisar a chefia quando um cliente ficou tempo demais sem resposta de ninguém.',
    unidade: 'horas',
    zeroDesliga: 'Não avisar',
  },
  supervisao_fila_horas: {
    grupo: 'supervisao',
    nome: 'Conversa parada na fila',
    objetivo: 'Avisar a chefia quando uma conversa ficou tempo demais sem ninguém assumir.',
    unidade: 'horas',
    zeroDesliga: 'Não avisar',
  },
  fuso_horario: {
    grupo: 'fuso',
    nome: 'Fuso horário',
    objetivo: 'Definir em que relógio o sistema conta as horas.',
  },
  lote_intervalo_min_segundos: {
    grupo: 'disparo',
    nome: 'Espera mínima entre mensagens',
    objetivo: 'Definir o piso do intervalo entre duas mensagens do mesmo lote.',
    unidade: 'segundos',
  },
  lote_intervalo_max_segundos: {
    grupo: 'disparo',
    nome: 'Espera máxima entre mensagens',
    objetivo: 'Definir o teto do intervalo entre duas mensagens do mesmo lote.',
    unidade: 'segundos',
  },
  lote_teto_diario: {
    grupo: 'disparo',
    nome: 'Teto de mensagens por dia',
    objetivo: 'Limitar quantas mensagens de lote o número dispara em um mesmo dia.',
    unidade: 'mensagens',
  },
  /*
    Morava no grupo Carteira, que ficou vazio na ADR 0070: é a ponta da
    distribuição, e por isso fica no Atendimento.
  */
  setor_padrao_id: {
    grupo: 'atendimento',
    nome: 'Setor que recebe quem chega sem dono',
    objetivo: 'Definir para onde vai o cliente que chega sem vendedor.',
  },
  historico_dias_para_reabrir: {
    grupo: 'importacao',
    nome: 'Folga para reabrir conversa antiga',
    objetivo: 'Decidir quais conversas trazidas de fora chegam pendentes de resposta.',
    unidade: 'dias',
  },
}

/**
 * Fusos que fazem sentido para uma empresa brasileira.
 *
 * O campo era texto livre, e um "America/Sao Paulo" com espaço no lugar do
 * sublinhado derruba o horário de todo mundo em silêncio: a hora passa a ser
 * lida no fuso do servidor e ninguém liga uma coisa à outra.
 */
export const FUSOS_HORARIOS: { valor: string; rotulo: string }[] = [
  { valor: 'America/Sao_Paulo', rotulo: 'Brasília, São Paulo, Sul e Sudeste (GMT-3)' },
  { valor: 'America/Bahia', rotulo: 'Salvador, Bahia (GMT-3)' },
  { valor: 'America/Fortaleza', rotulo: 'Fortaleza e Nordeste (GMT-3)' },
  { valor: 'America/Belem', rotulo: 'Belém, Pará (GMT-3)' },
  { valor: 'America/Manaus', rotulo: 'Manaus, Amazonas (GMT-4)' },
  { valor: 'America/Cuiaba', rotulo: 'Cuiabá, Mato Grosso (GMT-4)' },
  { valor: 'America/Campo_Grande', rotulo: 'Campo Grande, Mato Grosso do Sul (GMT-4)' },
  { valor: 'America/Porto_Velho', rotulo: 'Porto Velho, Rondônia (GMT-4)' },
  { valor: 'America/Rio_Branco', rotulo: 'Rio Branco, Acre (GMT-5)' },
  { valor: 'America/Noronha', rotulo: 'Fernando de Noronha (GMT-2)' },
]

export const salvarConfiguracaoSchema = z.object({
  chave: z.string().min(1).max(80),
  valor: z.unknown(),
})

export interface ConfiguracaoDto {
  chave: string
  valor: unknown
  atualizadoEm: string
}

export function validarValorConfiguracao(chave: string, valor: unknown) {
  const schema = (CHAVES_CONFIGURACAO as Record<string, z.ZodTypeAny | undefined>)[chave]
  if (!schema) return { ok: false as const, erro: `chave de configuração desconhecida: ${chave}` }

  const resultado = schema.safeParse(valor)
  if (!resultado.success) {
    return { ok: false as const, erro: resultado.error.issues[0]?.message ?? 'valor inválido' }
  }
  return { ok: true as const, valor: resultado.data as unknown }
}
